import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { FadeUp, MaskLineScroll } from "../components/Reveal";
import { BRAND, IMAGES } from "../lib/brand";

const CATS = [
  {
    id: "collision",
    label: "Collision & Insurance",
    subtitle: "Everyday vehicles, repaired right.",
    items: [
      "Insurance collision repair",
      "Customer-pay collision repair",
      "Body-panel repair",
      "Body-panel replacement",
      "Paint matching",
      "Refinishing",
      "Structural & frame assessment",
      "Insurance coordination",
      "Reassembly & final inspection",
    ],
    img: IMAGES.paintReflection,
  },
  {
    id: "paint",
    label: "Paint & Refinishing",
    subtitle: "From spot-blend to complete color change.",
    items: [
      "Spot & panel refinishing",
      "Complete vehicle refinishing",
      "Complete color changes",
      "Custom paint",
      "Multi-stage finishes",
      "Specialty paint applications",
      "Clear-coat refinishing",
      "Paint correction",
    ],
    img: IMAGES.paintBooth,
  },
  {
    id: "specialty",
    label: "JDM & Specialty Vehicles",
    subtitle: "Vehicles ordinary shops decline.",
    items: [
      "Imported Japanese vehicles",
      "Discontinued models",
      "Rare replacement panels",
      "Modified vehicles",
      "Custom body components",
      "Restorations",
      "JDM paint matching",
      "Specialty repair planning",
    ],
    img: IMAGES.servicesSpecialty,
  },
  {
    id: "motorsport",
    label: "Motorsport",
    subtitle: "Race, track & drift bodywork.",
    items: [
      "Drift-car bodywork",
      "Race-car repair",
      "Track-car refinishing",
      "Fiberglass components",
      "Carbon-fiber components",
      "Lightweight panels",
      "Aftermarket body parts",
      "Event-damage repair",
      "Cosmetic race preparation",
    ],
    img: IMAGES.drift,
  },
  {
    id: "specialty-projects",
    label: "Specialty Projects",
    subtitle: "Motorcycles, wheels, oversized & custom.",
    items: [
      "Motorcycles",
      "Wheel refinishing",
      "Custom fabrication",
      "Rust repair",
      "Oversized vehicles",
      "RV refinishing",
      "Specialty commercial projects",
    ],
    img: IMAGES.wheels,
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Services — Collision, Custom Paint, JDM & Motorsport Bodywork | ProForm Denver"
        description="Explore ProForm's paint, body, refinishing, JDM, race, drift, and specialty services in Denver, Colorado. Insurance workflows and custom builds."
        canonical={`https://${BRAND.domain}/services`}
      />

      <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-ink-700">
        <div className="wrap grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="label mb-3">— Services · 001</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
              <MaskLineScroll>Every service,</MaskLineScroll>
              <MaskLineScroll delay={0.08}>the <span className="text-proform-red">same standard</span>.</MaskLineScroll>
            </h1>
          </div>
          <div className="md:col-span-7 md:pt-8">
            <p className="text-chrome-300 text-lg leading-relaxed max-w-2xl">
              ProForm operates across two adjacent worlds: everyday collision and insurance repair, and specialty work on imported, modified, race, and drift vehicles. Both receive the same disciplined preparation, alignment, and finish.
            </p>
            <p className="mt-6 label">
              Project acceptance depends on vehicle condition, parts availability, schedule, scope, and repair requirements.
            </p>
          </div>
        </div>
      </section>

      {CATS.map((c, idx) => (
        <section id={c.id} key={c.id} className={`py-20 md:py-28 border-b border-ink-700 ${idx % 2 ? "bg-ink-900" : ""}`}>
          <div className="wrap grid md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-5">
              <FadeUp>
                <div className="clip-frame aspect-[4/5]">
                  <img src={c.img} alt={`${c.label} — atmospheric imagery`} loading="lazy" />
                </div>
              </FadeUp>
            </div>
            <div className="md:col-span-7 md:pt-4">
              <div className="label mb-3">— Category · 0{idx + 1}</div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">{c.label}</h2>
              <p className="mt-4 text-chrome-300 max-w-xl text-lg">{c.subtitle}</p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {c.items.map((it) => (
                  <li key={it} className="border-t border-ink-700 pt-3 flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 bg-proform-red flex-none" aria-hidden />
                    <span className="text-chrome-100">{it}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/estimate" className="btn btn-primary">Request an Estimate →</Link>
                <a href={`tel:${BRAND.phoneRaw}`} className="btn">Call {BRAND.phoneDisplay}</a>
              </div>
              <p className="mt-6 label">Some services require in-person inspection to confirm project fit.</p>
            </div>
          </div>
        </section>
      ))}
    </>
  );
}
